import { semanticColors } from '@moderneinc/design-system-tokens';
import {
  Alert,
  type AlertProps,
  AlertTitle,
  Box,
  Snackbar,
  type SnackbarOrigin
} from '@mui/material';
import React, {
  type FunctionComponent,
  type ReactElement,
  type ReactNode,
  useState
} from 'react';

type NotificationContextProps = {
  children: ReactNode[] | ReactNode;
};

type Severity = AlertProps['severity'];

type NotificationOptions = {
  title?: string;
  placement?: SnackbarOrigin;
  maxWidth?: number | string;
  // When set, the body is bounded to this height and scrolls — keeps a long
  // notification (e.g. a list of failures) from growing past the viewport.
  maxHeight?: number | string;
  // Auto-hide delay in ms; pass null to keep the notification open until the
  // user dismisses it (e.g. an error with a long list they need to read).
  duration?: number | null;
};

/**
 * PERF: actions only. Snackbar state in the value re-rendered ~47 consumers
 * per toast, every mounted diff-file header among them.
 */
export type NotificationContextState = {
  renderNotification: (
    severity: Severity,
    notification: ReactElement | string,
    options?: NotificationOptions
  ) => void;
  resetState: () => void;
};

type NotificationState = {
  open: boolean;
  notification: ReactElement | string;
  severity?: Severity;
  options: NotificationOptions;
};

const DEFAULT_OPTIONS: NotificationOptions = {
  placement: { vertical: 'bottom', horizontal: 'right' },
  duration: 6000,
  maxWidth: '100%'
};

const HIDDEN: NotificationState = {
  open: false,
  notification: null,
  options: DEFAULT_OPTIONS
};

export const NotificationContext =
  React.createContext<NotificationContextState>({
    // biome-ignore lint/suspicious/noEmptyBlockStatements: initial state
    renderNotification: () => {},
    // biome-ignore lint/suspicious/noEmptyBlockStatements: initial state
    resetState: () => {}
  });

/**
 * Provides a context for rendering notifications in the form of alerts in a
 * snackbar.
 */
export const NotificationProvider: FunctionComponent<
  NotificationContextProps
> = ({ children }) => {
  const [state, setState] = useState<NotificationState>(HIDDEN);
  const [actions] = useState<NotificationContextState>(() => ({
    renderNotification: (severity, notification, options = {}) => {
      setState({
        open: true,
        notification,
        severity,
        options: { ...DEFAULT_OPTIONS, ...options }
      });
    },
    // Keeps the alert mounted through the exit transition; nulling it there
    // fades out an empty Snackbar pill.
    resetState: () => {
      setState((previous) => ({ ...previous, open: false }));
    }
  }));

  return (
    <NotificationContext.Provider value={actions}>
      {children}
      <Snackbar
        autoHideDuration={state.options.duration}
        anchorOrigin={state.options.placement}
        open={state.open}
        onClose={actions.resetState}
      >
        {state.notification && (
          <Alert
            elevation={6}
            variant="outlined"
            // Flipping card surface — a hardcoded white toast left the
            // (theme-aware, light) alert text washed out in dark mode.
            sx={{ backgroundColor: semanticColors.color.surface.raised }}
            severity={state.severity}
            onClose={actions.resetState}
          >
            {state.options.title && (
              <AlertTitle>{state.options.title}</AlertTitle>
            )}
            <Box
              sx={{
                maxWidth: state.options.maxWidth,
                maxHeight: state.options.maxHeight,
                overflowY: state.options.maxHeight ? 'auto' : undefined
              }}
            >
              {state.notification}
            </Box>
          </Alert>
        )}
      </Snackbar>
    </NotificationContext.Provider>
  );
};
